// View2D.jsx — Techninis brėžinys (20 patobulinimų)

import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { Canvas, useThree } from '@react-three/fiber';
import { Text, Line } from '@react-three/drei';
import * as THREE from 'three';
import { makeStyles, tokens, ToggleButton } from '@fluentui/react-components';
import CaptureFrameNotifier from '../utils/CaptureFrameNotifier';
import { enumeratePanels } from '../utils/enumeratePanels';
import {
    SHELF_PIN_PITCH, SHELF_PIN_MARGIN, SHELF_PIN_RADIUS,
    HINGE_CUP_RADIUS, HINGE_CUP_INSET, HINGE_END_MARGIN, hingeCountForHeight,
    DRAWER_SLIDE_HOLE_MARGIN,
} from '../utils/hardwareStandards';


const useToolbarStyles = makeStyles({
    zoomSlider: {
        width: '70px',
        marginLeft: tokens.spacingHorizontalS,
        marginRight: tokens.spacingHorizontalS,
    },
    // "Baldas sienoje" / "Tik baldas" — standartinis Fluent ToggleButton pora
    // vietoj rankinio <button style={{...}}> (kuris turėjo ir realų bug'ą:
    // showWall===i===0 visada false, tad "aktyvaus" mygtuko žalias fonas
    // niekad neįsijungdavo). Žr. /admin/bugs "sutvarkyk baldas sienoje ir tik
    // baldas ir grazesnius standartinius mygtukus".
    wallToggleGroup: { display: 'flex', gap: tokens.spacingHorizontalXXS },
});

// ─── Konstantos ──────────────────────────────────────────────────────────────
const THICKNESS = 0.018;

// ── Teksto aukščių pakopos (ISO 3098 principas) ────────────────────────────
// ISO 3098 apibrėžia braižymo teksto aukščių progresiją fiksuotu žingsniu √2
// (tas pats santykis, kuriuo A4 popierius tampa A3). Šis brėžinys nespausdinamas
// fiksuotu masteliu ant popieriaus, tad tikrų mm reikšmių naudoti tiesiogiai
// negalime — bet laikomės TO PATIES žingsnio tarp keturių reikšmės pakopų,
// kiekvienai priskirdami elementus pagal jų funkciją (ne "iš akies"):
//   HEADING   — grupės/baldo pavadinimas (didžiausia — tai antraštė, ne duomuo)
//   PRIMARY   — tikri matmenys (DimensionLine) ir kabineto identifikavimas
//   SECONDARY — antrinės antraštės, simboliai, aprašomosios žymos
//   MICRO     — kompaktiškas apskritime įrėmintas nuorodos numeris (įprasta
//               braižymo praktika — "balloon" numeriai visada mažesni už
//               greta esantį tekstą)
const ISO_TEXT_HEADING   = 0.12;
const ISO_TEXT_PRIMARY   = ISO_TEXT_HEADING / Math.SQRT2;
const ISO_TEXT_SECONDARY = ISO_TEXT_PRIMARY / Math.SQRT2;
const ISO_TEXT_MICRO     = ISO_TEXT_SECONDARY / Math.SQRT2;

// ── Matmenų linijų tarpai (ISO 129 principas) ──────────────────────────────
// ISO 129: gretimos (chain/nested) matmenų linijos turi tolygų, teksto
// aukščiui proporcingą tarpą — ne savavališką koeficientą — kad linijos
// nesusidurtų su ankstesnės linijos teksto/rodyklių sritimi. Vidinė
// (per-kabineto) linija arčiausiai objekto, grupės linija — vienu žingsniu
// toliau.
const DIM_LINE_STEP      = ISO_TEXT_PRIMARY * 1.6;
const CABINET_DIM_OFFSET = DIM_LINE_STEP;
const GROUP_DIM_OFFSET   = DIM_LINE_STEP * 2;

// Vietos kiekis aplink patį baldą matmenų linijoms/žymoms, kad kamera jų
// neapkarpytų — turi apimti toliausiai išorėn nubrėžtą liniją
// (GROUP_DIM_OFFSET) + jos teksto poslinkį dar toliau išorėn (žr.
// ISO_TEXT_PRIMARY*2.1/1.5 poslinkį DimensionLine viduje žemiau — čia
// naudojame šiek tiek dosnesnį 2.2 koeficientą kaip saugumo atsargą).
const DIM_CLEARANCE  = GROUP_DIM_OFFSET + ISO_TEXT_PRIMARY * 2.2;
const DIM_LEFT_PAD   = 1.0;
const DIM_BOTTOM_PAD = 0.7;

// ── Furnitūros gręžimo standartas (32mm sistema) ───────────────────────────
// Konstantos (SHELF_PIN_*, HINGE_CUP_*, hingeCountForHeight) perkeltos į
// ../utils/hardwareStandards.js — VIENINTelis šaltinis, dabar bendras su
// calculatePrice.js (vyrių kiekio suderinimas) ir enumeratePanels.js
// (gamybinio brėžinio skylių pozicijos). Žr. tą failą dėl standarto šaltinio.

// ─── Pagalbinės funkcijos ─────────────────────────────────────────────────────

// Sekcijos ribų skaičiavimas (kaip SingleCabinet3D.jsx)
const getSectionBounds = (structure, path, innerWidth, innerHeight, wallThickness) => {
    const parts = path.split('-').slice(1);
    let node = structure || {};
    let x = 0, y = 0, w = innerWidth, h = innerHeight;
    for (const part of parts) {
        const i = parseInt(part);
        if (!node.split || !node.children) break;
        const isH = node.split === 'h';
        const ratio = node.ratio ?? 0.5;
        const available = isH ? h - wallThickness : w - wallThickness;
        const child0Size = available * ratio;
        const child1Size = available * (1 - ratio);
        if (isH) {
            if (i === 0) { y += child1Size + wallThickness; h = child0Size; }
            else { h = child1Size; }
        } else {
            if (i === 1) { x += child0Size + wallThickness; w = child1Size; }
            else { w = child0Size; }
        }
        node = node.children[i];
    }
    return { x, y, width: w, height: h };
};

// Pagrindo tipo pavadinimas (#6)
const getBasisLabel = (type, heightCm) => {
    const labels = ['', 'Kojos', 'Pakabinamas', 'Plokštė', 'Nėra pagrindo', 'Integruotas'];
    return labels[type] ? `${labels[type]} ${heightCm}cm` : '';
};

// Durelių tipo etiketė
const DOOR_LABELS = ['', 'Durys K', 'Durys D', 'Dvigubos', 'Atv. Viršus', 'Atv. Apačia', 'Stalčius'];
const DOOR_COLORS_2D = ['', '#1565C0', '#1565C0', '#1565C0', '#6A1B9A', '#6A1B9A', '#2E7D32'];

// Sekcijų vidinio elemento etiketė
const sectionOptionLabel = (idx) => {
    const m = { 1:'Kabykla', 2:'Dviguba Kabykla', 3:'Daliklis', 4:'Stalčius', 5:'Stalčių Blokas D',
                6:'Stalčių Blokas K', 7:'Dvigubas Stalčius', 8:'Ištraukiama Kabykla',
                9:'Pantografas', 10:'Reg. Lentyna', 11:'Lentyna su Bėgeliais' };
    return m[idx] || '';
};

// ─── Tekstūros ────────────────────────────────────────────────────────────────
const useHatchTexture = () => useMemo(() => {
    if (typeof document === 'undefined') return null;
    const size = 512;
    const canvas = document.createElement('canvas');
    canvas.width = size; canvas.height = size;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, size, size);
    ctx.strokeStyle = '#000'; ctx.lineWidth = 3; ctx.lineCap = 'square';
    ctx.beginPath();
    for (let i = -size; i < size * 2; i += 128) {
        ctx.moveTo(i, size); ctx.lineTo(i + size, 0);
    }
    ctx.stroke();
    const tex = new THREE.CanvasTexture(canvas);
    tex.wrapS = THREE.RepeatWrapping; tex.wrapT = THREE.RepeatWrapping;
    tex.minFilter = THREE.LinearMipmapLinearFilter; tex.magFilter = THREE.LinearFilter;
    tex.anisotropy = 16;
    return tex;
}, []);

// ─── HatchBoard ───────────────────────────────────────────────────────────────
const HatchBoard = ({ width, height, position }) => {
    const texture = useHatchTexture();
    const mat = useMemo(() => {
        if (!texture) return null;
        const c = texture.clone(); c.needsUpdate = true;
        c.repeat.set(width * 10, height * 10); return c;
    }, [texture, width, height]);
    if (!mat) return null;
    return (
        <group position={position}>
            <mesh>
                <planeGeometry args={[width, height]} />
                <meshBasicMaterial map={mat} toneMapped={false} transparent side={THREE.DoubleSide} />
            </mesh>
            <lineSegments>
                <edgesGeometry args={[new THREE.BoxGeometry(width, height, 0)]} />
                <lineBasicMaterial color="black" />
            </lineSegments>
        </group>
    );
};

// ─── #18 Унifed MeasurementArrow (pakeičia VerticalArrow) ─────────────────────
const MeasurementArrow = ({ length, position, text, orientation = 'vertical' }) => {
    const arrowSize = 0.02;
    const isV = orientation === 'vertical';
    const [px, py] = position;

    const ArrowHead = ({ pos, flip }) => {
        const s = new THREE.Shape();
        if (isV) {
            s.moveTo(0,0); s.lineTo(-arrowSize, flip ? arrowSize*2 : -arrowSize*2); s.lineTo(arrowSize, flip ? arrowSize*2 : -arrowSize*2); s.lineTo(0,0);
        } else {
            s.moveTo(0,0); s.lineTo(flip ? -arrowSize*2 : arrowSize*2, -arrowSize); s.lineTo(flip ? -arrowSize*2 : arrowSize*2, arrowSize); s.lineTo(0,0);
        }
        return <mesh position={[pos[0], pos[1], 0]}><shapeGeometry args={[s]} /><meshBasicMaterial color="black" side={THREE.DoubleSide} /></mesh>;
    };

    const p1 = isV ? [px, py - length/2 + 0.02] : [px - length/2 + 0.02, py];
    const p2 = isV ? [px, py + length/2 - 0.02] : [px + length/2 - 0.02, py];

    return (
        <group position={[0, 0, 0.05]}>
            <Line points={[p1, p2]} color="black" lineWidth={0.5} />
            <ArrowHead pos={isV ? [px, py + length/2 - 0.01] : [px + length/2 - 0.01, py]} flip={false} />
            <ArrowHead pos={isV ? [px, py - length/2 + 0.01] : [px - length/2 + 0.01, py]} flip={true} />
            <mesh position={isV ? [px, py, 0.01] : [px, py, 0.01]}><planeGeometry args={[isV ? ISO_TEXT_SECONDARY*2.5 : ISO_TEXT_SECONDARY*3, ISO_TEXT_SECONDARY*1.4]} /><meshBasicMaterial color="white" toneMapped={false} side={THREE.DoubleSide} /></mesh>
            <Text position={isV ? [px, py, 0.02] : [px, py, 0.02]} fontSize={ISO_TEXT_SECONDARY} color="black" anchorX="center" anchorY="middle" rotation={[0, 0, isV ? 0 : 0]}>{text}</Text>
        </group>
    );
};

// ─── DimensionLine ─────────────────────────────────────────────────────────────
const DimensionLine = ({ start, end, text, offset = 0, orientation = 'vertical', isInternal = false }) => {
    const witnessLen = isInternal ? 0.02 : Math.abs(offset) + 0.05;
    let p1, p2, textPos, bgPos, textRotation;
    let w1s, w1e, w2s, w2e;
    const sign = Math.sign(offset || 1);

    if (orientation === 'horizontal') {
        p1 = [start[0], start[1] - offset, 0]; p2 = [end[0], end[1] - offset, 0];
        const midX = (start[0]+end[0])/2; const midY = start[1] - offset - (isInternal ? 0 : ISO_TEXT_PRIMARY*1.5);
        textPos = [midX, midY, 0.1]; bgPos = [midX, midY, 0.05]; textRotation = 0;
        w1s = [start[0], start[1], 0]; w1e = [start[0], start[1] - witnessLen*sign, 0];
        w2s = [end[0],   end[1],   0]; w2e = [end[0],   end[1]   - witnessLen*sign, 0];
    } else {
        p1 = [start[0]-offset, start[1], 0]; p2 = [end[0]-offset, end[1], 0];
        textRotation = isInternal ? 0 : Math.PI/2;
        const midX = start[0] - offset - (isInternal ? 0 : ISO_TEXT_PRIMARY*2.1);
        const midY = (start[1]+end[1])/2;
        textPos = [midX, midY, 0.1]; bgPos = [midX, midY, 0.05];
        w1s = [start[0], start[1], 0]; w1e = [start[0] - witnessLen*sign, start[1], 0];
        w2s = [end[0],   end[1],   0]; w2e = [end[0]   - witnessLen*sign, end[1],   0];
    }

    const DashedLine = ({ points: pts }) => {
        const segs = []; const ds = 0.02; const gs = 0.01;
        for (let i=0; i<pts.length-1; i++) {
            const s = new THREE.Vector3(...pts[i]); const e = new THREE.Vector3(...pts[i+1]);
            const dir = new THREE.Vector3().subVectors(e,s); const len = dir.length(); dir.normalize();
            let cur = 0;
            while (cur < len) {
                const a = s.clone().add(dir.clone().multiplyScalar(cur)); cur = Math.min(cur+ds, len);
                const b = s.clone().add(dir.clone().multiplyScalar(cur)); cur += gs;
                segs.push([a.toArray(), b.toArray()]);
            }
        }
        return <>{segs.map((d,i) => <Line key={i} points={d} color="black" lineWidth={0.5} />)}</>;
    };

    const TArrow = ({ pos, rot }) => {
        const sz = 0.02; const sh = new THREE.Shape();
        sh.moveTo(0,0); sh.lineTo(-sz,-sz*2); sh.lineTo(sz,-sz*2); sh.lineTo(0,0);
        return <mesh position={pos} rotation={[0,0,rot]}><shapeGeometry args={[sh]} /><meshBasicMaterial color="black" side={THREE.DoubleSide} /></mesh>;
    };

    return (
        <group>
            <Line points={[p1, p2]} color="black" lineWidth={1} />
            {!isInternal && <><DashedLine points={[w1s, w1e]} /><DashedLine points={[w2s, w2e]} /></>}
            {orientation === 'horizontal'
                ? <><TArrow pos={[p1[0],p1[1],0]} rot={Math.PI/2} /><TArrow pos={[p2[0],p2[1],0]} rot={-Math.PI/2} /></>
                : <><TArrow pos={[p1[0],p1[1],0]} rot={Math.PI} /><TArrow pos={[p2[0],p2[1],0]} rot={0} /></>
            }
            <mesh position={bgPos} rotation={[0,0,textRotation]}><planeGeometry args={[ISO_TEXT_PRIMARY*2.25, ISO_TEXT_PRIMARY*1.2]} /><meshBasicMaterial color="white" toneMapped={false} side={THREE.DoubleSide} /></mesh>
            <Text position={textPos} fontSize={ISO_TEXT_PRIMARY} color="black" anchorX="center" anchorY="middle" rotation={[0,0,textRotation]}>{text}</Text>
        </group>
    );
};

// ─── Simboliai ────────────────────────────────────────────────────────────────
const DrawerSymbol = ({ width, height, position }) => {
    const [x,y] = position; const hw = width/2-0.05; const hh = height/2-0.05;
    return (<group>
        <Line points={[[x-hw,y+hh,0],[x+hw,y-hh,0]]} color="#555" lineWidth={0.5} />
        <Line points={[[x-hw,y-hh,0],[x+hw,y+hh,0]]} color="#555" lineWidth={0.5} />
    </group>);
};
const RodSymbol = ({ width, height, position }) => {
    const [x,y] = position; const lineY = y + height/2 - 0.08;
    return <Line points={[[x-width/2+0.05, lineY, 0],[x+width/2-0.05, lineY, 0]]} color="#555" lineWidth={2} />;
};

// ─── #14 Durelių lanko simbolis ───────────────────────────────────────────────
// Piešia puslankį nurodančią atsidarymo kryptį (standartinis simbolis techniniuose brėžiniuose)
const DoorArcSymbol = ({ x, y, w, h, doorType }) => {
    if (!doorType) return null;
    const segs = 16;
    const arc = (cx, cy, r, a0, a1) => {
        const pts = [];
        for (let i = 0; i <= segs; i++) {
            const a = a0 + (a1-a0)*(i/segs);
            pts.push([cx + r*Math.cos(a), cy + r*Math.sin(a), 0.03]);
        }
        return pts;
    };

    const col = DOOR_COLORS_2D[doorType] || '#1565C0';
    // Durelių/stalčiaus KONTŪRAS (išorinis stačiakampis) visada juodas — kaip ir
    // visi kiti baldo kontūrai (korpuso sienelės, pagrindas) — vartotojo prašymu.
    // Spalvinis kodavimas (col) paliktas TIK durų tipo simboliui (varstymo lankui/
    // vyrių pusei/stalčiaus linijoms) ir tekstinei etiketei, kad tipas atpažįstamas
    // iš karto, bet pats kontūras nesusilieja su bendra juoda brėžinio linija.
    const outlineColor = '#000000';
    const lw = 1;

    // Durelių kontūras
    const outline = [[x,y,0.02],[x+w,y,0.02],[x+w,y+h,0.02],[x,y+h,0.02],[x,y,0.02]];

    if (doorType === 1) { // Kairė — vyriai dešinėje, lankas į kairę
        return (<group>
            <Line points={outline} color={outlineColor} lineWidth={lw} />
            <Line points={arc(x+w, y, h, Math.PI/2, Math.PI)} color={col} lineWidth={0.5} />
            <Line points={[[x,y,0.02],[x+w,y,0.02]]} color={outlineColor} lineWidth={lw} />
        </group>);
    }
    if (doorType === 2) { // Dešinė — vyriai kairėje, lankas į dešinę
        return (<group>
            <Line points={outline} color={outlineColor} lineWidth={lw} />
            <Line points={arc(x, y, h, 0, Math.PI/2)} color={col} lineWidth={0.5} />
        </group>);
    }
    if (doorType === 3) { // Dvigubos
        const mid = x + w/2;
        return (<group>
            <Line points={outline} color={outlineColor} lineWidth={lw} />
            <Line points={[[mid,y,0.02],[mid,y+h,0.02]]} color={col} lineWidth={0.4} dashed />
            <Line points={arc(x,    y, h/2, 0, Math.PI/2)} color={col} lineWidth={0.5} />
            <Line points={arc(x+w,  y, h/2, Math.PI/2, Math.PI)} color={col} lineWidth={0.5} />
        </group>);
    }
    if (doorType === 4) { // Atverčiamos viršus
        return (<group>
            <Line points={outline} color={outlineColor} lineWidth={lw} />
            <Line points={arc(x+w/2, y+h, w/2, Math.PI, 2*Math.PI)} color={col} lineWidth={0.5} />
        </group>);
    }
    if (doorType === 5) { // Atverčiamos apačia
        return (<group>
            <Line points={outline} color={outlineColor} lineWidth={lw} />
            <Line points={arc(x+w/2, y, w/2, 0, Math.PI)} color={col} lineWidth={0.5} />
        </group>);
    }
    if (doorType === 6) { // Stalčius
        return (<group>
            <Line points={outline} color={outlineColor} lineWidth={lw} />
            <Line points={[[x+w*0.2,y+h*0.35,0.02],[x+w*0.8,y+h*0.35,0.02]]} color={col} lineWidth={0.8} />
            <Line points={[[x+w*0.2,y+h*0.65,0.02],[x+w*0.8,y+h*0.65,0.02]]} color={col} lineWidth={0.8} />
        </group>);
    }
    return <Line points={outline} color={outlineColor} lineWidth={lw} />;
};

// ─── Lentynų kaiščių skylių eilė (32mm sistema) ──────────────────────────────
// Vertikalaus skydelio (šoninės sienelės ar vidinės pertvaros) kaiščių skylių
// eilė piešiama kaip stačiakampėje projekcijoje matoma taškų kolona ties
// skydelio ašimi — gylio kryptimi (priekinė/galinė eilė) šis vaizdas
// neatskiria, tad abi eilės sutampa vienoje X koordinatėje (tai atitinka
// realų braižymo supaprastinimą, naudojamą fasadinio vaizdo brėžiniuose).
const ShelfPinHoleColumn = ({ x, bottomY, topY }) => {
    const usable = topY - bottomY - SHELF_PIN_MARGIN * 2;
    if (usable <= 0) return null;
    const count = Math.floor(usable / SHELF_PIN_PITCH) + 1;
    const holes = Array.from({ length: count }, (_, i) => bottomY + SHELF_PIN_MARGIN + i * SHELF_PIN_PITCH);
    return (
        <group>
            {holes.map((hy, i) => (
                <mesh key={i} position={[x, hy, 0.03]}>
                    <circleGeometry args={[SHELF_PIN_RADIUS, 8]} />
                    <meshBasicMaterial color="#666" toneMapped={false} />
                </mesh>
            ))}
        </group>
    );
};

// ─── Durų vyrių tašelių žyma ──────────────────────────────────────────────────
// Nubraižo Ø35mm vyrio tašelės apskritimus ties tikra vyrio puse (nustatoma iš
// durų atsidarymo krypties — tas pats susitarimas kaip DoorArcSymbol) ir
// tikru vyrių skaičiumi/pozicijomis pagal durų aukštį.
const HingeCupRow = ({ edgeX, sign, y, h }) => {
    const n = hingeCountForHeight(h);
    const margin = Math.min(HINGE_END_MARGIN, h / (2 * (n - 1 || 1)));
    const step = n > 1 ? (h - margin * 2) / (n - 1) : 0;
    const cx = edgeX + sign * HINGE_CUP_INSET;
    const positions = Array.from({ length: n }, (_, i) => y + margin + i * step);
    return (
        <group>
            {positions.map((hy, i) => (
                <mesh key={i} position={[cx, hy, 0.035]}>
                    <ringGeometry args={[HINGE_CUP_RADIUS * 0.72, HINGE_CUP_RADIUS, 20]} />
                    <meshBasicMaterial color="#555" toneMapped={false} />
                </mesh>
            ))}
        </group>
    );
};

// ─── Vidinių pertvarų piešimas (#3) ──────────────────────────────────────────
const RecursivePartitions2D = ({ node, width, height, x, y, showDims, leafData }) => {
    if (!node || !node.split || !node.children) return null;
    const isH = node.split === 'h';
    const ratio = node.ratio ?? 0.5;
    const innerH = height - THICKNESS;
    const innerW = width  - THICKNESS;
    const c0H = isH ? innerH * ratio       : height;
    const c1H = isH ? innerH * (1-ratio)   : height;
    const c0W = isH ? width                : innerW * ratio;
    const c1W = isH ? width                : innerW * (1-ratio);
    const boardX = isH ? 0       : c0W;
    const boardY = isH ? c1H     : 0;
    const c0X = 0; const c0Y = isH ? c1H + THICKNESS : 0;
    const c1X = isH ? 0 : c0W + THICKNESS; const c1Y = 0;
    return (
        <group position={[x, y, 0]}>
            <HatchBoard
                width={isH ? width : THICKNESS}
                height={isH ? THICKNESS : height}
                position={[boardX+(isH?width/2:THICKNESS/2), boardY+(isH?THICKNESS/2:height/2), 0.005]}
            />
            {!isH && <ShelfPinHoleColumn x={boardX+THICKNESS/2} bottomY={0} topY={height} />}
            {showDims && isH && (
                <Text position={[width+0.1, boardY+THICKNESS/2+c0H/2, 0.05]} fontSize={ISO_TEXT_SECONDARY} color="#333" anchorX="left" anchorY="middle">
                    {`${(c0H*100).toFixed(0)}cm`}
                </Text>
            )}
            {showDims && !isH && (
                <Text position={[c0W/2, -0.12, 0.05]} fontSize={ISO_TEXT_SECONDARY} color="#333" anchorX="center" anchorY="middle">
                    {`${(c0W*100).toFixed(0)}cm`}
                </Text>
            )}
            <RecursivePartitions2D node={node.children[0]} width={c0W} height={c0H} x={c0X} y={c0Y} showDims={showDims} leafData={leafData} />
            <RecursivePartitions2D node={node.children[1]} width={c1W} height={c1H} x={c1X} y={c1Y} showDims={showDims} leafData={leafData} />
        </group>
    );
};

// ─── Cabinet2D ─────────────────────────────────────────────────────────────────
const Cabinet2D = ({
    cabinet, positionX, width, height, index, cabinetsInGroup,
    basisType, basisHeight, sectionOptions, snapshotMode,
    doorColors, mergedSections, doorHandles,
    cabNumber, onSectionClick, hideInterior = false, hideDoors = false,
    leftSideElement, rightSideElement,
}) => {
    // Ankstesnė formulė ((basisType===4||basisType===5)?0:basisHeight) buvo pasenusi —
    // liko iš laikų prieš SingleCabinet3D.jsx "Pagrindas" perrašymus (tipas 0 "Nepasirinkta"
    // turi būti 0, o tipas 4 "Dešinė iki grindų" TURI turėti normalią pagrindo zoną,
    // kaip ir 1/2/3/6/7/8 — ankstesnė formulė klaidingai nulindavo JĮ vieną iš keturių
    // "turi pagrindą" tipų). Nesutapimas su tikru 3D korpusu reiškė, kad šis brėžinys
    // (ir per jį — vyrių kiekio skaičiavimas calculatePrice.js, žr. ten cabVisualBaseHeight)
    // durų aukštį galėjo skaičiuoti neteisingai bet kuriam baldui su pagrindo zona (žr.
    // ClickUp "2. Furnitūra. Lankstai. Skaičiavimas nesilaiko taisyklės"). Dabar TA PATI
    // formulė, kaip SingleCabinet3D.jsx.
    // basisType 5 "Be pagrindo": 0.015 (ne 0.1) — suderinta su SingleCabinet3D.jsx
    // vėlesniu pataisymu (ClickUp "2.0 Baldas. Pagrindas. Be pagrindo. Panaikinti
    // apatinę lentyną..."), kuris sumažino šio tipo tarpą iki grindų nuo 10cm iki
    // 1,5cm — ši reikšmė čia liko nepakeista per tą pataisymą ir vėl buvo nebesutampanti.
    // basisType 7/8: rezervuota tik plonos plokštės storis (THICKNESS), ne pilnas
    // basisHeight — durys/vidus turi prasidėti nuo plokštės viršaus, be tarpo (žr.
    // SingleCabinet3D.jsx identišką komentarą prie visualBaseHeight).
    const visualBaseHeight = basisType === 0 ? 0 : (basisType === 5 ? 0.015
        : ((basisType === 7 || basisType === 8) ? THICKNESS : basisHeight));
    const carcassHeight = Math.max(0.001, height - visualBaseHeight);
    const innerBottomY = visualBaseHeight + THICKNESS;
    const innerTopY    = height - THICKNESS;
    const innerUsableH = Math.max(0.001, innerTopY - innerBottomY);
    const innerWidth   = width - THICKNESS * 2;
    const fid = cabinet.furnitureId;

    const sortedShelves = cabinet.shelves
        ? [...cabinet.shelves].sort((a,b) => a.position[1]-b.position[1]) : [];

    // Sekcijų skaičiavimas (iš lentynų)
    const sections = [];
    let curY = innerBottomY;
    sortedShelves.forEach((shelf, idx) => {
        const sy = Number(shelf.position[1]) || 0;
        const sh = Math.max(0.001, sy - THICKNESS/2 - curY);
        if (sh > 0) sections.push({ index: idx, startY: curY, height: sh });
        curY = sy + THICKNESS/2;
    });
    const lastH = Math.max(0.001, innerTopY - curY);
    if (lastH > 0) sections.push({ index: sortedShelves.length, startY: curY, height: lastH });

    // Durelių grupės skaičiavimas (#1)
    const cabMerged = (mergedSections || []).filter(s => s.furnitureId === fid && s.cabinetIndex === index);
    const groupIds = [...new Set(cabMerged.map(s => s.groupId))];
    const doorRects = [];
    groupIds.forEach(gid => {
        const entries = cabMerged.filter(s => s.groupId === gid);
        const allPaths = new Set();
        entries.forEach(s => { allPaths.add(s.sectionIndex); allPaths.add(s.targetSectionIndex); });
        let minX=Infinity, minY=Infinity, maxX=-Infinity, maxY=-Infinity;
        for (const p of allPaths) {
            const b = getSectionBounds(cabinet.structure, p, innerWidth, innerUsableH, THICKNESS);
            if (!b) continue;
            minX=Math.min(minX,b.x); minY=Math.min(minY,b.y);
            maxX=Math.max(maxX,b.x+b.width); maxY=Math.max(maxY,b.y+b.height);
        }
        if (!isFinite(minX)) return;
        const primaryPath = entries[0].sectionIndex;
        const doorKey = `${fid}-${index}-${primaryPath}`;
        const doorType = (doorColors || {})[doorKey] || 0;
        if (!doorType) return;
        // Koordinatės brėžinyje (+ vidinės erdvės offsets)
        const dx = THICKNESS + minX;
        const dy = innerBottomY + minY;
        doorRects.push({ x: dx, y: dy, w: maxX-minX, h: maxY-minY, doorType, key: gid });
    });

    // Solo durelės (be grupės)
    const mergedPathSet = new Set([...cabMerged.map(s=>s.sectionIndex), ...cabMerged.map(s=>s.targetSectionIndex)]);
    Object.entries(doorColors||{}).forEach(([key, dt]) => {
        if (!dt) return;
        const prefix = `${fid}-${index}-`;
        if (!key.startsWith(prefix)) return;
        const path = key.slice(prefix.length);
        if (mergedPathSet.has(path)) return;
        const b = getSectionBounds(cabinet.structure, path, innerWidth, innerUsableH, THICKNESS);
        if (!b) return;
        doorRects.push({ x: THICKNESS+b.x, y: innerBottomY+b.y, w: b.width, h: b.height, doorType: dt, key: `solo-${key}` });
    });

    // Sekcijų numeriai (global numeravimas pradedamas nuo cabNumber*100 + i)
    let sectionSeq = (cabNumber || 0) * 100 + 1;

    return (
        <group position={[positionX, 0, 0]}>
            {/* #6 Pagrindo žyma */}
            {!snapshotMode && visualBaseHeight > 0 && (
                <Text position={[width/2, visualBaseHeight/2, 0.08]} fontSize={ISO_TEXT_SECONDARY} color="#555" anchorX="center" anchorY="middle">
                    {getBasisLabel(basisType, (basisHeight*100).toFixed(0))}
                </Text>
            )}
            {/* Pagrindas */}
            {visualBaseHeight > 0 && basisType !== 5 && (
                <HatchBoard width={width} height={visualBaseHeight} position={[width/2, visualBaseHeight/2, 0]} />
            )}
            {/* Sienos */}
            {index === 0 && <HatchBoard width={THICKNESS} height={carcassHeight} position={[THICKNESS/2, visualBaseHeight+carcassHeight/2, 0]} />}
            <HatchBoard width={THICKNESS} height={carcassHeight} position={[width-THICKNESS/2, visualBaseHeight+carcassHeight/2, 0]} />
            <HatchBoard width={width} height={THICKNESS} position={[width/2, height-THICKNESS/2, 0]} />
            <HatchBoard width={width} height={THICKNESS} position={[width/2, visualBaseHeight+THICKNESS/2, 0]} />

            {/* #2.2 Kairys/Dešinys šonas — falšjuosta/falšfasadas/papildomas šonas kontūras.
                Rodoma nepriklausomai nuo to, KURIS iš 4 SideElementDialog pasirinkimų
                aktyvus (bet kokia reikšmė !== 0), kad brėžinyje visada matytųsi, kur šis
                elementas užimtų baldo priekį — juodas kontūras, kaip ir visi kiti baldo
                kontūrai. Plotis atitinka TĄ PATĮ skaičiavimą, kaip FurnitureGroup.jsx
                renderSideElements/leftSideElementWidth (18mm arba falšfasado custom plotis). */}
            {index === 0 && leftSideElement && leftSideElement.type !== 0 && (() => {
                const elemW = (leftSideElement.type === 2 && leftSideElement.width) ? leftSideElement.width / 100 : THICKNESS;
                return (
                    <Line points={[[0,0,0.15],[elemW,0,0.15],[elemW,height,0.15],[0,height,0.15],[0,0,0.15]]}
                        color="#000000" lineWidth={1.5} dashed dashSize={0.02} gapSize={0.015} />
                );
            })()}
            {index === cabinetsInGroup.length - 1 && rightSideElement && rightSideElement.type !== 0 && (() => {
                const elemW = (rightSideElement.type === 2 && rightSideElement.width) ? rightSideElement.width / 100 : THICKNESS;
                return (
                    <Line points={[[width-elemW,0,0.15],[width,0,0.15],[width,height,0.15],[width-elemW,height,0.15],[width-elemW,0,0.15]]}
                        color="#000000" lineWidth={1.5} dashed dashSize={0.02} gapSize={0.015} />
                );
            })()}

            {/* Lentynų kaiščių skylės ant šoninių sienelių */}
            {!hideInterior && (
                <>
                    {index === 0 && <ShelfPinHoleColumn x={THICKNESS/2} bottomY={innerBottomY} topY={innerTopY} />}
                    <ShelfPinHoleColumn x={width-THICKNESS/2} bottomY={innerBottomY} topY={innerTopY} />
                </>
            )}

            {/* Lentynos */}
            {!hideInterior && sortedShelves.map((shelf,i) => (
                <HatchBoard key={i} width={innerWidth} height={THICKNESS} position={[width/2, Number(shelf.position[1])||0, 0]} />
            ))}

            {/* #3 Vidinės pertvaros su dimensijomis */}
            {!hideInterior && cabinet.structure?.split && (
                <group position={[THICKNESS, visualBaseHeight+THICKNESS, 0]}>
                    <RecursivePartitions2D
                        node={cabinet.structure}
                        width={innerWidth}
                        height={Math.max(0.001, height-visualBaseHeight-THICKNESS*2)}
                        x={0} y={0}
                        showDims={!snapshotMode}
                    />
                </group>
            )}

            {/* #5 Sekcijų simboliai, numeriai, matmenys */}
            {!hideInterior && sections.map((sec, i) => {
                const optKey = `${fid}-${index}-${sec.index}`;
                const optIdx = (sectionOptions||{})[optKey] || 0;
                const cx = width/2; const cy = sec.startY + sec.height/2;
                const num = sectionSeq++;

                return (
                    <group key={i}>
                        {/* Simboliai */}
                        {[4,5,6,7].includes(optIdx) && <DrawerSymbol width={innerWidth} height={sec.height} position={[cx, cy, 0.01]} />}
                        {[1,2,8,9].includes(optIdx) && <RodSymbol width={innerWidth} height={sec.height} position={[cx, cy, 0.01]} />}
                        {optIdx > 0 && ![1,2,3,4,5,6,7,8,9,10,11].includes(optIdx) && (
                            <Text position={[cx, cy, 0.02]} fontSize={ISO_TEXT_SECONDARY} color="black" anchorX="center" anchorY="middle">{sectionOptionLabel(optIdx)}</Text>
                        )}

                        {/* #5 Sekcijos numeris — kompaktiškas "balloon" tipo nuorodos numeris */}
                        {!snapshotMode && (
                            <group position={[THICKNESS+0.04, cy, 0.06]}>
                                <mesh><circleGeometry args={[ISO_TEXT_MICRO*1.1, 16]} /><meshBasicMaterial color="white" /></mesh>
                                <lineSegments><edgesGeometry args={[new THREE.CircleGeometry(ISO_TEXT_MICRO*1.1, 16)]} /><lineBasicMaterial color="#09aa89" /></lineSegments>
                                <Text fontSize={ISO_TEXT_MICRO} color="#09aa89" anchorX="center" anchorY="middle">{num}</Text>
                            </group>
                        )}

                        {/* #16 Sekcijos aukščio rodyklė (visoms, net mažoms — adaptyvus šriftas) */}
                        {!snapshotMode && (
                            <MeasurementArrow
                                length={sec.height}
                                position={[width+0.15, cy]}
                                text={`${(sec.height*100).toFixed(0)}`}
                                orientation="vertical"
                            />
                        )}

                        {/* #19 Interaktyvus paspaudimas */}
                        {onSectionClick && (
                            <mesh
                                position={[cx, cy, 0.04]}
                                onClick={(e) => { e.stopPropagation(); onSectionClick({ furnitureId: fid, cabinetIndex: index, sectionIdx: sec.index }); }}
                            >
                                <planeGeometry args={[innerWidth, sec.height]} />
                                <meshBasicMaterial transparent opacity={0} />
                            </mesh>
                        )}
                    </group>
                );
            })}

            {/* #1 #14 Durelės */}
            {!hideDoors && doorRects.map(dr => (
                <group key={dr.key}>
                    <DoorArcSymbol x={dr.x} y={dr.y} w={dr.w} h={dr.h} doorType={dr.doorType} />
                    {/* Vyrių tašelės — vyrio pusė atitinka DoorArcSymbol lanko susitarimą */}
                    {dr.doorType === 1 && <HingeCupRow edgeX={dr.x+dr.w} sign={-1} y={dr.y} h={dr.h} />}
                    {dr.doorType === 2 && <HingeCupRow edgeX={dr.x} sign={1} y={dr.y} h={dr.h} />}
                    {dr.doorType === 3 && (
                        <>
                            <HingeCupRow edgeX={dr.x} sign={1} y={dr.y} h={dr.h} />
                            <HingeCupRow edgeX={dr.x+dr.w} sign={-1} y={dr.y} h={dr.h} />
                        </>
                    )}
                    {!snapshotMode && (
                        <Text position={[dr.x+dr.w/2, dr.y+dr.h/2, 0.05]} fontSize={ISO_TEXT_SECONDARY} color={DOOR_COLORS_2D[dr.doorType]||'#1565C0'} anchorX="center" anchorY="middle">
                            {DOOR_LABELS[dr.doorType]||''}
                        </Text>
                    )}
                </group>
            ))}
        </group>
    );
};

// ─── #4 Šoninis vaizdas su lentynomis ─────────────────────────────────────────
const SideView = ({ height, depth, positionX, wallDepthRight, roomShape, cabinets }) => {
    const effectiveDepth = Math.max(depth, wallDepthRight);
    // Surenka unikalias lentynų pozicijas iš visų kabinetų
    const shelfYs = useMemo(() => {
        const ys = new Set();
        (cabinets||[]).forEach(c => (c.shelves||[]).forEach(s => ys.add(Number(s.position[1])||0)));
        return [...ys];
    }, [cabinets]);
    const basisH = Number(cabinets?.[0]?.basisHeight) || 0;
    const bt = Number(cabinets?.[0]?.basisType) || 0;
    const vBase = (bt===4||bt===5) ? 0 : basisH;

    return (
        <group position={[positionX + 0.5, 0, 0]}>
            <HatchBoard width={effectiveDepth} height={height} position={[effectiveDepth/2, height/2, 0]} />
            {/* Lentynos iš šono */}
            {shelfYs.map((y,i) => (
                <Line key={i} points={[[0,y,0.01],[effectiveDepth,y,0.01]]} color="#555" lineWidth={1} />
            ))}
            {/* Pagrindas iš šono */}
            {vBase > 0 && <Line points={[[0,vBase,0.01],[effectiveDepth,vBase,0.01]]} color="#333" lineWidth={1.5} />}
            {/* Gylio (apatinė) matmens linija naudoja TĄ PATĮ GROUP_DIM_OFFSET kaip
                pagrindinio vaizdo baldo grupių pločio linija (žr. žemiau) — anksčiau
                čia buvo atskiras, priešingo ženklo poslinkis (-0.3), todėl šoninio
                vaizdo gylis ir pagrindinio vaizdo plotis atsidurdavo skirtingose
                aukštyse, o ne vienoje bendroje apatinėje linijoje. */}
            <DimensionLine start={[0,0,0]} end={[effectiveDepth,0,0]} offset={GROUP_DIM_OFFSET} orientation="horizontal" text={`${(effectiveDepth*100).toFixed(0)} cm`} />
            <DimensionLine start={[0,0,0]} end={[0,height,0]}        offset={GROUP_DIM_OFFSET} orientation="vertical"   text={`${(height*100).toFixed(0)} cm`} />
            <Text position={[effectiveDepth/2, height+0.12, 0]} fontSize={ISO_TEXT_SECONDARY} color="#333" anchorX="center" anchorY="middle">Šoninis vaizdas</Text>
        </group>
    );
};

// ─── Gamybinis brėžinys — kiekvienas skydelis ATSKIRAI ────────────────────────
// Gręžimas vyksta gamykloje pagal REALIAI užsakytą furnitūrą — šis
// dokumentas neturi tikslo nurodyti TIKSLIOS gręžimo specifikacijos (skylių
// skaičiaus, žingsnio), tik VIETĄ/ZONĄ, kur konkretaus tipo tvirtinimas bus
// gręžiamas. Todėl vietoj tikslaus kiekvienos skylės taško (pvz. pilnos
// 32mm-žingsnio taškų kolonos ar tankios varžtų eilės) piešiama VIENA
// kompaktiška zonos žyma — du kraštiniai taškai + juos jungianti punktyrinė
// linija (vertikaliam diapazonui) arba analogiškai horizontaliam.
const DrillMark = ({ x, y }) => (
    <mesh position={[x, y, 0.03]}>
        <circleGeometry args={[0.006, 10]} />
        <meshBasicMaterial color="#888" toneMapped={false} />
    </mesh>
);

const DrillZoneV = ({ x, bottomY, topY }) => (
    <group>
        <DrillMark x={x} y={bottomY} />
        <DrillMark x={x} y={topY} />
        <Line points={[[x, bottomY, 0.025], [x, topY, 0.025]]} color="#aaa" lineWidth={0.6} dashed />
    </group>
);

const DrillZoneH = ({ y, leftX, rightX }) => (
    <group>
        <DrillMark x={leftX} y={y} />
        <DrillMark x={rightX} y={y} />
        <Line points={[[leftX, y, 0.025], [rightX, y, 0.025]]} color="#aaa" lineWidth={0.6} dashed />
    </group>
);

// PANEL_GAP/CODE_SPACE/DIM_SPACE — tarpai tarp išklotinės skydelių.
const PANEL_GAP = 0.08;
const PANEL_CODE_SPACE = 0.12;
const PANEL_DIM_SPACE = 0.06;
const CODE_BADGE_R = 0.045;

// Paprasta "eilutėmis" išklotinė (NE optimalus pjovimo išdėstymas — tai
// ORIENTACINIS gamybinis dokumentas, ne medžiagos ekonomijos CNC nesting
// įrankis). Skydeliai rikiuojami mažėjančio ploto tvarka ir dedami eilutėmis,
// kiekvienos eilutės aukštis = didžiausio jos skydelio aukštis + vietos
// kodo ženkliukui/matmenims. Kadangi ant brėžinio dabar rašomas TIK trumpas
// kodas (ne pilnas pavadinimas — žr. enumeratePanels.js/PanelsTable
// SummaryView.jsx), stulpelio plotis nebereikalauja teksto pločio įverčio,
// pakanka nedidelio fiksuoto ženkliuko pločio rezervo.
export const layoutPanels = (panels) => {
    if (!panels.length) return { items: [], totalWidth: 1, totalHeight: 1 };
    const cols = Math.max(2, Math.round(Math.sqrt(panels.length * 1.3)));
    const sorted = [...panels].sort((a, b) => (b.width * b.height) - (a.width * a.height));
    const rows = [];
    for (let i = 0; i < sorted.length; i += cols) rows.push(sorted.slice(i, i + cols));

    const rowHeights = rows.map(row => Math.max(...row.map(p => p.height)) + PANEL_CODE_SPACE + PANEL_DIM_SPACE);
    const totalHeight = rowHeights.reduce((s, h) => s + h, 0) + PANEL_GAP * Math.max(0, rows.length - 1);

    const items = [];
    let cursorYTop = totalHeight;
    let totalWidth = 0;
    rows.forEach((row, ri) => {
        const rh = rowHeights[ri];
        let x = 0;
        row.forEach((panel) => {
            const slotW = Math.max(panel.width, CODE_BADGE_R * 2 + 0.02);
            const y = cursorYTop - rh + PANEL_DIM_SPACE;
            items.push({ panel, x: x + (slotW - panel.width) / 2, y });
            x += slotW + PANEL_GAP;
        });
        totalWidth = Math.max(totalWidth, x - PANEL_GAP);
        cursorYTop -= rh + PANEL_GAP;
    });
    return { items, totalWidth, totalHeight };
};

// Kodo "balionėlis" virš kiekvieno skydelio — TAS PATS vizualinis susitarimas,
// kaip Cabinet2D sekcijos numerio žymos (žr. View2DScene/Cabinet2D aukščiau):
// baltas apskritimas + spalvotas kontūras + numeris. Pilnas pavadinimas,
// matmenys, medžiaga ir gręžimo vieta — TIK lentelėje (SummaryView.jsx
// PanelsTable), kurią šis kodas nurodo.
const ProductionPanel = ({ panel, x, y }) => (
    <group position={[x, y, 0]}>
        <HatchBoard width={panel.width} height={panel.height} position={[panel.width / 2, panel.height / 2, 0]} />
        <group position={[panel.width / 2, panel.height + CODE_BADGE_R + 0.02, 0.06]}>
            <mesh><circleGeometry args={[CODE_BADGE_R, 20]} /><meshBasicMaterial color="white" toneMapped={false} /></mesh>
            <lineSegments><edgesGeometry args={[new THREE.CircleGeometry(CODE_BADGE_R, 20)]} /><lineBasicMaterial color="#09aa89" /></lineSegments>
            <Text fontSize={ISO_TEXT_PRIMARY * 0.8} color="#09aa89" anchorX="center" anchorY="middle">{panel.code}</Text>
        </group>
        <Text position={[panel.width / 2, -0.04, 0.05]} fontSize={ISO_TEXT_SECONDARY * 0.85} color="#777" anchorX="center" anchorY="middle">
            {`${(panel.width * 100).toFixed(1)} × ${(panel.height * 100).toFixed(1)} cm`}
        </Text>
        {panel.holes?.shelfPinColumns && (
            <>
                <DrillZoneV x={SHELF_PIN_MARGIN} bottomY={SHELF_PIN_MARGIN} topY={Math.max(SHELF_PIN_MARGIN, panel.height - SHELF_PIN_MARGIN)} />
                <DrillZoneV x={panel.width - SHELF_PIN_MARGIN} bottomY={SHELF_PIN_MARGIN} topY={Math.max(SHELF_PIN_MARGIN, panel.height - SHELF_PIN_MARGIN)} />
            </>
        )}
        {(panel.holes?.hingePlates || []).map((hp, i) => (
            <DrillMark key={i} x={hp.offset} y={hp.y} />
        ))}
        {(panel.holes?.drawerSlides || []).map((ds, i) => (
            <DrillZoneH key={i} y={ds.y} leftX={DRAWER_SLIDE_HOLE_MARGIN} rightX={Math.max(DRAWER_SLIDE_HOLE_MARGIN, panel.width - DRAWER_SLIDE_HOLE_MARGIN)} />
        ))}
        {(panel.holes?.hingeCups || []).map((hc, i) => (
            <HingeCupRow key={i} edgeX={hc.side === 'left' ? 0 : panel.width} sign={hc.side === 'left' ? 1 : -1} y={0} h={panel.height} />
        ))}
    </group>
);

// ─── Gamybinis brėžinys — visų VIENO baldo vieneto skydelių išklotinė ────────
// Naudojama TIK per productionMode (žr. View2DScene/View2D žemiau) — vietoj
// surinktos elevacijos piešia KIEKVIENĄ fizinį skydelį atskirai, su realiais
// pjovimo matmenimis ir furnitūros gręžimo skylių žymomis (žr.
// enumeratePanels.js dėl geometrijos/skylių šaltinio).
const ProductionPanelSheet = ({ allCabinets, mergedSections, doorColors, sectionOptions }) => {
    const panels = useMemo(
        () => enumeratePanels(allCabinets, { mergedSections, doorColors, sectionOptions, wallThickness: THICKNESS }),
        [allCabinets, mergedSections, doorColors, sectionOptions]
    );
    const { items } = useMemo(() => layoutPanels(panels), [panels]);
    return (
        <group>
            {items.map(({ panel, x, y }) => <ProductionPanel key={panel.id} panel={panel} x={x} y={y} />)}
        </group>
    );
};

// ─── #8 Mastelio juosta ────────────────────────────────────────────────────────
const ScaleBar = ({ x, y, pixelsPerMeter }) => {
    const barM = 0.5; // 50 cm baro ilgis
    return (
        <group position={[x, y, 0.1]}>
            <Line points={[[0,0,0],[barM,0,0]]} color="black" lineWidth={2} />
            <Line points={[[0,-0.03,0],[0,0.03,0]]} color="black" lineWidth={1.5} />
            <Line points={[[barM,-0.03,0],[barM,0.03,0]]} color="black" lineWidth={1.5} />
            <Text position={[barM/2,-0.07,0]} fontSize={ISO_TEXT_SECONDARY} color="black" anchorX="center">50 cm</Text>
        </group>
    );
};

// ─── Camera Controller (#15 panning palaikymas) ───────────────────────────────
const CameraController = ({ userZoomPercent, baseZoom, contentCenterX, contentCenterY, panX, panY }) => {
    const { camera, invalidate } = useThree();
    useEffect(() => {
        if (camera.isOrthographicCamera) {
            camera.zoom = baseZoom * (userZoomPercent/100);
            camera.position.set(contentCenterX+panX, contentCenterY+panY, 100);
            camera.lookAt(contentCenterX+panX, contentCenterY+panY, 0);
            camera.updateProjectionMatrix();
            // frameloop="demand" (žr. <Canvas> žemiau) — ši kameros pozicija keičiama
            // imperatyviai, ne per R3F valdomą JSX prop'ą, tad be šio invalidate()
            // naujas zoom/pan niekada nebūtų faktiškai nupieštas.
            invalidate();
        }
    }, [userZoomPercent, baseZoom, camera, contentCenterX, contentCenterY, panX, panY, invalidate]);
    return null;
};

// ─── Pagrindinė 2D scena ──────────────────────────────────────────────────────
const View2DScene = ({
    allCabinets, wallWidth, wallHeight, wallDepth, wallDepthRight, roomShape,
    sectionOptions, showWall, snapshotMode, furnitureType, slidingDoorCount,
    doorColors, mergedSections, doorHandles, onSectionClick, hideInterior, hideDoors,
    productionMode,
}) => {
    const safeAll = useMemo(() => Array.isArray(allCabinets) ? allCabinets : [], [allCabinets]);
    const wallWm = wallWidth/100, wallHm = wallHeight/100;
    const wallDm = wallDepth/100, wallDRm = wallDepthRight/100;

    const furnitureGroups = useMemo(() => {
        const g = {};
        safeAll.forEach(cab => {
            const fid = cab.furnitureId;
            if (!g[fid]) g[fid] = { id:fid, cabinets:[], x:Number(cab.x)||0, y:Number(cab.y)||0, width:0, height:0, depth:0 };
            g[fid].cabinets.push(cab);
            g[fid].x      = Math.min(g[fid].x, Number(cab.x)||0);
            g[fid].y      = Math.min(g[fid].y, Number(cab.y)||0);
            g[fid].width  = Math.max(g[fid].width,  (Number(cab.x)||0)+(Number(cab.width)||0)) - g[fid].x;
            g[fid].height = Math.max(g[fid].height, (Number(cab.y)||0)+(Number(cab.height)||0)) - g[fid].y;
            g[fid].depth  = Math.max(g[fid].depth,  Number(cab.depth)||0);
        });
        return Object.values(g);
    }, [safeAll]);

    // A saved furniture piece is very commonly built from several SEPARATE
    // furnitureId'd cabinet modules stacked in the same visual column purely
    // for manufacturing reasons (e.g. a short upper cabinet sitting on top of
    // a tall base cabinet) — furnitureGroups above treats each as its own
    // independent group. Dimensioning each one separately produced numbers
    // like "89cm" for the upper module and "205cm" for the lower one instead
    // of the ONE continuous "294cm" a person actually reads a column's height
    // as — and since object key order (hence furnitureGroups' array order)
    // often interleaves upper/lower modules across different columns rather
    // than listing one column fully before the next, a simple "compare to the
    // previous array entry" dedup could never detect that two modules were
    // even part of the same column. This groups by actual (x, width) instead,
    // which is what "same column" really means, and is used for BOTH the
    // width and height dimension lines below (a stacked module's own width is
    // just as redundant to repeat as its height).
    const columnStacks = useMemo(() => {
        const cols = [];
        furnitureGroups.forEach((g) => {
            const col = cols.find(c => Math.abs(c.x - g.x) < 0.005 && Math.abs(c.width - g.width) < 0.005);
            if (col) {
                col.minY = Math.min(col.minY, g.y);
                col.maxY = Math.max(col.maxY, g.y + g.height);
            } else {
                cols.push({ x: g.x, width: g.width, minY: g.y, maxY: g.y + g.height });
            }
        });
        cols.sort((a, b) => a.x - b.x);
        return cols;
    }, [furnitureGroups]);

    let overallMaxX = wallWm;
    let overallMaxY = wallHm;
    furnitureGroups.forEach(g => {
        overallMaxX = Math.max(overallMaxX, g.x+g.width);
        overallMaxY = Math.max(overallMaxY, g.y+g.height);
    });

    const extOff = 0.5;
    // Sienos plotis (horizontali apatinė matmenų linija) turi stovėti PO VISŲ
    // baldo lygmens matmenų eilučių (GROUP_DIM_OFFSET/CABINET_DIM_OFFSET stack'as
    // po pačiu baldu), o ne tarp jų — extOff (0.5) buvo mažesnis už tą realiai
    // užimamą plotą, tad linija "užlipdavo" ant baldo brėžinio apatinių matmenų
    // (žr. /admin/bugs "Sienos plotis turetu buti apacioje viso brezinio o ne ant
    // jo"). DIM_BOTTOM_PAD jau yra TA PATI riba, kurią naudoja kameros rėminimas
    // (minY žemiau) ir ScaleBar pozicija — t.y. jau žinoma "visos apatinės zonos"
    // riba; sienos pločio linija dabar stovi lygiai prie jos, kaip ir ScaleBar.
    const wallWidthDimOffset = DIM_BOTTOM_PAD - 0.05;
    const sideX = overallMaxX + 0.8;
    const maxH = furnitureGroups.reduce((m,g) => Math.max(m,g.height), 0);
    const effDepth = Math.max(wallDm, wallDRm);

    // Gamybinis brėžinys — visai kita scena (kiekvieno skydelio išklotinė),
    // ne surinkta elevacija/siena/šoninis vaizdas. Tikrinama ČIA (po visų
    // hook'ų aukščiau), nes React Hooks reikalauja, kad hook'ai būtų
    // kviečiami TA PAČIA tvarka kiekviename render'e — ankstyvas return
    // PRIEŠ furnitureGroups/columnStacks useMemo būtų juos praleidęs
    // productionMode atveju, pažeidžiant rules-of-hooks.
    if (productionMode) {
        return (
            <ProductionPanelSheet
                allCabinets={safeAll}
                mergedSections={mergedSections}
                doorColors={doorColors}
                sectionOptions={sectionOptions}
            />
        );
    }

    return (
        <group>
            {/* ── Siena ── */}
            <group>
                {showWall && !snapshotMode && <HatchBoard width={wallWm} height={wallHm} position={[wallWm/2, wallHm/2, 0]} />}
                {!snapshotMode && <DimensionLine start={[0,0,0]} end={[wallWm,0,0]} offset={-wallWidthDimOffset} orientation="horizontal" text={`${(wallWm*100).toFixed(0)} cm`} />}
                {!snapshotMode && <DimensionLine start={[0,0,0]} end={[0,wallHm,0]} offset={-extOff} orientation="vertical"   text={`${(wallHm*100).toFixed(0)} cm`} />}

                {/* Šoninių sienų (Niša/Prie kairės/Prie dešinės) hatch'as PAŠALINTAS —
                    dubliuodavo tą patį šoninį pjūvį, kurį jau rodo atskiras, tinkamai
                    pažymėtas "Šoninis vaizdas" (SideView) dešinėje — vartotojui atrodė
                    kaip trys skirtingi šoniniai brėžiniai vietoj vieno (žr. /admin/bugs
                    "Siu breziniu nereikia, turi buti tik vienas soninis pacio baldo").
                    "Gylis: X cm" tekstas dėl tos pačios priežasties jau anksčiau
                    pašalintas — gylis matomas Šoninio vaizdo matmens linijoje. */}
            </group>

            {/* ── Baldų grupės ── */}
            {furnitureGroups.map((group) => {
                let cabOffsetX = 0;
                const isSld = furnitureType === 1;
                const N = Math.max(2, slidingDoorCount||2);
                const overlapW = Math.min(0.04, group.width*0.03);
                const doorW = group.width/N + overlapW;
                const doorPositions = Array.from({length:N},(_,i)=> N>1?(i/(N-1))*(group.width-doorW):0);

                return (
                    <group key={group.id} position={[group.x, group.y, 0.01]}>
                        {group.cabinets.map((cab, ci) => {
                            const posX = cabOffsetX;
                            cabOffsetX += Number(cab.width)||0;
                            return (
                                <group key={cab.uniqueId}>
                                    {/* #2 Individualaus kabineto plotis */}
                                    {!snapshotMode && group.cabinets.length > 1 && (
                                        <DimensionLine
                                            start={[posX, 0, 0]} end={[posX+(Number(cab.width)||0), 0, 0]}
                                            offset={CABINET_DIM_OFFSET} orientation="horizontal"
                                            text={`${((Number(cab.width)||0)*100).toFixed(0)}`}
                                        />
                                    )}
                                    <Cabinet2D
                                        cabinet={cab}
                                        positionX={posX}
                                        width={Number(cab.width)||0}
                                        height={Number(cab.height)||0}
                                        index={ci}
                                        cabinetsInGroup={group.cabinets}
                                        basisType={Number(cab.basisType)||0}
                                        basisHeight={Number(cab.basisHeight)||0}
                                        sectionOptions={sectionOptions||{}}
                                        snapshotMode={snapshotMode}
                                        doorColors={doorColors}
                                        mergedSections={mergedSections}
                                        doorHandles={doorHandles}
                                        cabNumber={ci}
                                        onSectionClick={onSectionClick}
                                        hideInterior={hideInterior}
                                        hideDoors={hideDoors}
                                        leftSideElement={cab.leftSideElement}
                                        rightSideElement={cab.rightSideElement}
                                    />
                                </group>
                            );
                        })}

                        {/* Stumdomos durys 2D */}
                        {isSld && (() => {
                            const firstCab = group.cabinets[0];
                            const bt = Number(firstCab?.basisType)||0;
                            const bh = Number(firstCab?.basisHeight)||0;
                            const doorBaseY = (bt===4||bt===5)?0:bh;
                            const doorH = group.height - doorBaseY;
                            const pad = 0.012;
                            return doorPositions.map((x,i) => {
                                const isFront = i%2===0;
                                const col = isFront ? '#3a3a3a' : '#888888';
                                return (
                                    <group key={i} position={[0,0,0.02+i*0.001]}>
                                        <mesh position={[x+doorW/2, doorBaseY+doorH/2, 0]}>
                                            <planeGeometry args={[doorW, doorH]} />
                                            <meshBasicMaterial color={isFront?'#c8d8e8':'#e8e8e8'} transparent opacity={0.18} side={THREE.DoubleSide} />
                                        </mesh>
                                        <Line points={[[x,doorBaseY,0],[x+doorW,doorBaseY,0],[x+doorW,doorBaseY+doorH,0],[x,doorBaseY+doorH,0],[x,doorBaseY,0]]} color={col} lineWidth={isFront?1.5:1.0} />
                                        <Line points={[[x+pad,doorBaseY+pad,0],[x+doorW-pad,doorBaseY+doorH-pad,0]]} color={col} lineWidth={0.8} />
                                    </group>
                                );
                            });
                        })()}
                    </group>
                );
            })}

            {/* ── Stulpelio (column-stack) matmenys ──────────────────────────
                Plotis rodomas VIENĄ kartą kiekvienam stulpeliui prie jo apatinio
                krašto, aukštis — VIENĄ kartą per visą stulpelio aukštį (nuo
                žemiausio iki aukščiausio jį sudarančio baldo vieneto), o ne
                atskirai kiekvienam moduliui (žr. columnStacks komentarą
                aukščiau). Sekantis skirtingo aukščio stulpelis piešiamas prie
                SAVO dešinio krašto, ne kairio — kairysis kraštas jau užimtas
                ANKSTESNIO, greta esančio stulpelio turinio. */}
            {!snapshotMode && columnStacks.map((col, i) => {
                const prevCol = columnStacks[i - 1];
                const colHeight = col.maxY - col.minY;
                const prevColHeight = prevCol ? prevCol.maxY - prevCol.minY : null;
                const showHeightDim = i === 0 || !prevCol || Math.abs(prevColHeight - colHeight) > 0.001;
                const heightDimOnRight = showHeightDim && i !== 0;
                return (
                    <group key={`col-dim-${col.x.toFixed(3)}`}>
                        <DimensionLine
                            start={[col.x, col.minY, 0]} end={[col.x + col.width, col.minY, 0]}
                            offset={GROUP_DIM_OFFSET} orientation="horizontal"
                            text={`${(col.width * 100).toFixed(0)} cm`}
                        />
                        {showHeightDim && !heightDimOnRight && (
                            <DimensionLine
                                start={[col.x, col.minY, 0]} end={[col.x, col.maxY, 0]}
                                offset={GROUP_DIM_OFFSET} orientation="vertical"
                                text={`${(colHeight * 100).toFixed(0)} cm`}
                            />
                        )}
                        {heightDimOnRight && (
                            <DimensionLine
                                start={[col.x + col.width, col.minY, 0]} end={[col.x + col.width, col.maxY, 0]}
                                offset={-GROUP_DIM_OFFSET} orientation="vertical"
                                text={`${(colHeight * 100).toFixed(0)} cm`}
                            />
                        )}
                    </group>
                );
            })}

            {/* #4 Šoninis vaizdas su lentynomis */}
            {!snapshotMode && (
                <SideView
                    height={Math.max(wallHm, maxH)}
                    depth={effDepth}
                    positionX={sideX}
                    wallDepthRight={wallDRm}
                    roomShape={roomShape}
                    cabinets={safeAll}
                />
            )}

            {/* #8 Mastelio juosta */}
            {!snapshotMode && <ScaleBar x={0} y={-DIM_BOTTOM_PAD+0.05} />}
        </group>
    );
};

// ─── Pagrindinis View2D komponentas ──────────────────────────────────────────
const View2D = ({
    allCabinets, wallWidth, wallHeight, wallDepth, wallDepthRight, roomShape,
    sectionOptions, doorColors, mergedSections, doorHandles,
    onRendererReady, onFramesRendered, frameKey, snapshotMode, furnitureType = 0, slidingDoorCount = 2,
    onSectionClick, defaultShowWall = true, hideInterior = false, hideDoors = false, hideToolbar = false,
    productionMode = false,
}) => {
    const toolbarStyles = useToolbarStyles();
    const [zoomPercent, setZoomPercent] = useState(100);
    const [showWall, setShowWall]       = useState(defaultShowWall);
    const [panX, setPanX]               = useState(0);
    const [panY, setPanY]               = useState(0);
    const containerRef = useRef(null);
    const rendererRef  = useRef(null);
    const panStartRef  = useRef(null);
    const [containerSize, setContainerSize] = useState({ width:800, height:600 });
    // Fiksavimo egzemplioriams (onFramesRendered) Canvas'o eventSource negali rodyti
    // į useRef, kurio .current dar nepritvirtintas — React commit'ina layout efektus
    // POST-ORDER tvarka (Canvas vidinis events.connect() efektas, kaip VAIKAS, suveikia
    // PRIEŠ šio apgaubiančio div'o, kaip TĖVO, ref'o pritvirtinimą), tad r3f visada gautų
    // null. Callback-ref + state: div'as sumontuojamas be Canvas, ref'as suveikia, TADA
    // Canvas sumontuojamas su eventSource, rodančiu į jau egzistuojantį DOM mazgą.
    const [captureEventSourceEl, setCaptureEventSourceEl] = useState(null);
    const setContainerRef = useCallback((node) => {
        containerRef.current = node;
        if (onFramesRendered) setCaptureEventSourceEl(node);
    }, [onFramesRendered]);

    const safeAll = useMemo(() => Array.isArray(allCabinets) ? allCabinets : [], [allCabinets]);

    useEffect(() => {
        if (!containerRef.current) return;
        const ro = new ResizeObserver(entries => {
            for (const e of entries) {
                const { width, height } = e.contentRect;
                if (width>0&&height>0) setContainerSize({ width, height });
            }
        });
        ro.observe(containerRef.current);
        return () => ro.disconnect();
    }, []);

    // Gamybinio brėžinio (productionMode) kameros ribos — VISAI KITOKS turinys
    // (skydelių išklotinė, ne siena/kabinetai), tad skaičiuojama iš tos pačios
    // layoutPanels() funkcijos, kurią naudoja ir pati ProductionPanelSheet.
    const productionPanels = useMemo(
        () => (productionMode ? enumeratePanels(safeAll, { mergedSections, doorColors, sectionOptions, wallThickness: 0.018 }) : null),
        [productionMode, safeAll, mergedSections, doorColors, sectionOptions]
    );
    const productionLayout = useMemo(
        () => (productionPanels ? layoutPanels(productionPanels) : null),
        [productionPanels]
    );

    const { totalContentWidth, totalContentHeight, contentCenterX, contentCenterY } = useMemo(() => {
        if (productionMode && productionLayout) {
            const pad = 0.3;
            return {
                totalContentWidth: productionLayout.totalWidth + pad * 2,
                totalContentHeight: productionLayout.totalHeight + pad * 2,
                contentCenterX: productionLayout.totalWidth / 2,
                contentCenterY: productionLayout.totalHeight / 2,
            };
        }
        const wm = wallWidth/100, hm = wallHeight/100, dm = wallDepth/100, drm = wallDepthRight/100;
        // Kai siena nerodoma (showWall=false — švarus baldo brėžinys be patalpos konteksto),
        // riba NEBŪTINA siekti visos patalpos pločio/aukščio, O minX/minY turi būti skaičiuojami
        // pagal PAČIO BALDO faktinę poziciją (kabinetai gali būti pastumti patalpoje, pvz.
        // x=2.4m), o ne fiksuotą "prie sienos" atstumą — priešingu atveju mažas ar nuo kairės
        // pastumtas baldas nuotraukoje atrodo sunykęs tuščioje erdvėje.
        let minX = showWall ? 0 : Infinity, minY = showWall ? 0 : Infinity;
        let maxX = showWall ? wm : -Infinity, maxY = showWall ? hm : -Infinity;
        safeAll.forEach(c => {
            const x = Number(c.x)||0, y = Number(c.y)||0;
            minX = Math.min(minX, x); minY = Math.min(minY, y);
            maxX = Math.max(maxX, x+(Number(c.width)||0));
            maxY = Math.max(maxY, y+(Number(c.height)||0));
        });
        if (!isFinite(minX)) { minX = 0; minY = 0; maxX = wm; maxY = hm; }
        // Šoninis vaizdas (SideView) visada piešiamas į dešinę nuo pagrindinio
        // vaizdo, nepriklausomai nuo showWall — todėl abu atvejai turi rezervuoti
        // jam vietos (0.8 tarpas + jo pločio, lygaus gyliui). showWall atveju tai
        // jau darė esamas terminas; be sienos atveju jo anksčiau trūko.
        const sideViewRoom = 0.8 + Math.max(dm,drm);
        maxX = maxX + sideViewRoom + (showWall ? 0 : DIM_CLEARANCE);
        minX = minX - (showWall ? DIM_LEFT_PAD : DIM_CLEARANCE);
        minY = minY - (showWall ? DIM_BOTTOM_PAD : DIM_CLEARANCE);
        // Matmenų linijų ir žymų aukščiausias taškas viršuje visada turi šiek
        // tiek atsargos — be jos kamera apkarpydavo viršutinius elementus, nes
        // maxY anksčiau apskritai nebuvo pildomas jokiu tarpu.
        maxY = maxY + DIM_CLEARANCE;
        const w = Math.max(showWall ? 2 : 0.6, maxX-minX), h = Math.max(showWall ? 1.5 : 0.6, maxY-minY);
        return { totalContentWidth:w, totalContentHeight:h, contentCenterX:(maxX+minX)/2, contentCenterY:(maxY+minY)/2 };
    }, [safeAll, wallWidth, wallHeight, wallDepth, wallDepthRight, showWall, productionMode, productionLayout]);

    const baseZoom = useMemo(() => {
        if (!totalContentWidth||!totalContentHeight) return 50;
        const pad = showWall ? 1.25 : 1.08;
        const zx = containerSize.width  / (totalContentWidth  * pad);
        const zy = containerSize.height / (totalContentHeight * pad);
        // 300 riba čia buvo parinkta įprastam vaizdui, kurio turinys (siena/
        // baldų grupė + šoninis vaizdas + matmenų linijos) beveik visada
        // siekia ≥2-3m — tad zx/zy retai viršydavo 300. Gamybinio brėžinio
        // (productionMode) turinys — vieno baldo vieneto skydelių išklotinė —
        // dažnai daug mažesnė (pvz. ~2.5×2.5m viena mažesnė spinta), tad
        // zx/zy legitimiai siekia 500-900+; 300 riba tokiu atveju priverstinai
        // sumažindavo mastelį ir palikdavo didelius tuščius laukus aplink
        // realiai smulkų turinį. Gamybiniam brėžiniui riba pakeliama.
        const cap = productionMode ? 900 : 300;
        return Math.min(zx, zy, cap);
    }, [totalContentWidth, totalContentHeight, containerSize, showWall, productionMode]);

    // #15 Scroll zoom
    const handleWheel = useCallback((e) => {
        e.preventDefault();
        setZoomPercent(z => Math.min(300, Math.max(30, z - e.deltaY * 0.08)));
    }, []);

    // #15 Panning
    const handlePointerDown = useCallback((e) => {
        if (e.button !== 1 && e.button !== 0) return; // middle or left
        panStartRef.current = { x: e.clientX, y: e.clientY, px: panX, py: panY };
        e.currentTarget.setPointerCapture(e.pointerId);
    }, [panX, panY]);

    const handlePointerMove = useCallback((e) => {
        if (!panStartRef.current) return;
        const zoom = baseZoom * (zoomPercent/100);
        const dx = (e.clientX - panStartRef.current.x) / zoom;
        const dy = (e.clientY - panStartRef.current.y) / zoom;
        setPanX(panStartRef.current.px - dx);
        setPanY(panStartRef.current.py + dy);
    }, [baseZoom, zoomPercent]);

    const handlePointerUp = useCallback(() => { panStartRef.current = null; }, []);

    return (
        <div style={{ width:'100%', height:'100%', display:'flex', flexDirection:'column', background:'#fff', border:'1px solid #ddd', borderRadius:8, overflow:'hidden' }}>
            {/* ── Toolbar ── */}
            {/* Be pavadinimo ir be zoom/eksporto mygtukų juostos (scroll'as canvas'e
                jau leidžia priartinti/atitolinti) — vien "Baldas sienoje"/"Tik baldas"
                perjungiklis, standartiniais Fluent ToggleButton (žr. /admin/bugs
                "Paslepk siuos mygtukus, istrink title techinis brezinys..."). */}
            {!hideToolbar && <div style={{ display:'flex', alignItems:'center', padding:'6px 14px', borderBottom:'1px solid #eee', flexShrink:0 }}>
                <div className={toolbarStyles.wallToggleGroup}>
                    {['Baldas sienoje', 'Tik baldas'].map((lbl, i) => (
                        <ToggleButton
                            key={i}
                            size="small"
                            appearance={showWall === (i === 0) ? 'primary' : 'subtle'}
                            checked={showWall === (i === 0)}
                            onClick={() => setShowWall(i === 0)}
                        >
                            {lbl}
                        </ToggleButton>
                    ))}
                </div>
            </div>}

            {/* ── Canvas ── */}
            <div
                ref={setContainerRef}
                style={{ flex:1, overflow:'hidden', position:'relative', cursor:'grab' }}
                onWheel={handleWheel}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
            >
                {(!onFramesRendered || captureEventSourceEl) && <Canvas
                    orthographic
                    camera={{ position:[0,0,100], zoom:50 }}
                    style={{ background:'white', width:'100%', height:'100%' }}
                    dpr={[1, 2]}
                    // See Builder3D.jsx's identical frameloop comment — this 2D canvas stays
                    // MOUNTED (just CSS display:none'd) whenever the 3D view is active, so
                    // without "demand" it keeps rendering every frame even while fully hidden.
                    frameloop={onFramesRendered ? 'always' : 'demand'}
                    gl={{ preserveDrawingBuffer:true, antialias:true }}
                    eventSource={onFramesRendered ? captureEventSourceEl : undefined}
                    onCreated={({ gl }) => { rendererRef.current=gl; if(onRendererReady) onRendererReady(gl); }}
                >
                    <CameraController
                        userZoomPercent={zoomPercent}
                        baseZoom={baseZoom}
                        contentCenterX={contentCenterX}
                        contentCenterY={contentCenterY}
                        panX={panX}
                        panY={panY}
                    />
                    {onFramesRendered && <CaptureFrameNotifier key={frameKey} onReady={onFramesRendered} />}
                    <View2DScene
                        allCabinets={safeAll}
                        wallWidth={wallWidth} wallHeight={wallHeight}
                        wallDepth={wallDepth} wallDepthRight={wallDepthRight}
                        roomShape={roomShape}
                        sectionOptions={sectionOptions}
                        showWall={showWall}
                        snapshotMode={snapshotMode}
                        furnitureType={furnitureType}
                        slidingDoorCount={slidingDoorCount}
                        doorColors={doorColors||{}}
                        mergedSections={mergedSections||[]}
                        doorHandles={doorHandles}
                        onSectionClick={onSectionClick}
                        hideInterior={hideInterior}
                        hideDoors={hideDoors}
                        productionMode={productionMode}
                    />
                </Canvas>}

            </div>
        </div>
    );
};

export default View2D;
